import type { CandidateRecord } from '../../types/market';

const DATABASE_NAME = 'pilum-trade-lab';
const DATABASE_VERSION = 1;
const EPISODE_STORE = 'candidateEpisodes';
const EVENT_STORE = 'candidateEvents';

export interface CandidateArchiveEvent {
  key: string;
  candidateId: string;
  symbol: string;
  timestamp: number;
  state: CandidateRecord['state'];
  direction: CandidateRecord['direction'];
  family: CandidateRecord['family'];
  score: number;
  price: number;
  freshIssuanceAllowed: boolean;
  deepValidated: boolean;
  validationStatus: CandidateRecord['validationStatus'];
  validationCoverage: number;
  validationIssueCount: number;
  reason: string;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is unavailable.'));
      return;
    }
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onerror = () => reject(request.error ?? new Error('Unable to open candidate archive.'));
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(EPISODE_STORE)) {
        const episodes = database.createObjectStore(EPISODE_STORE, { keyPath: 'id' });
        episodes.createIndex('symbol', 'symbol', { unique: false });
        episodes.createIndex('foundAt', 'foundAt', { unique: false });
      }
      if (!database.objectStoreNames.contains(EVENT_STORE)) {
        const events = database.createObjectStore(EVENT_STORE, { keyPath: 'key' });
        events.createIndex('candidateId', 'candidateId', { unique: false });
        events.createIndex('symbol', 'symbol', { unique: false });
        events.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

function meaningfulFingerprint(candidate: CandidateRecord): string {
  return [
    candidate.state,
    candidate.direction,
    candidate.family,
    Math.floor(candidate.candidateScore / 5),
    candidate.freshIssuanceAllowed ? 'fresh' : 'transition',
    candidate.validationStatus,
    Math.floor(candidate.validationCoverage * 10),
    candidate.validationIssues.length,
    candidate.primaryDriver,
  ].join('|');
}

const lastWrittenFingerprints = new Map<string, string>();

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
  });
}

export async function archiveCandidates(candidates: CandidateRecord[]): Promise<void> {
  if (candidates.length === 0 || typeof indexedDB === 'undefined') return;
  const database = await openDatabase();
  try {
    const pending = candidates.flatMap((candidate) => {
      const fingerprint = meaningfulFingerprint(candidate);
      return lastWrittenFingerprints.get(candidate.id) === fingerprint ? [] : [{ candidate, fingerprint }];
    });
    if (pending.length === 0) {
      database.close();
      return;
    }

    const transaction = database.transaction([EPISODE_STORE, EVENT_STORE], 'readwrite');
    const episodes = transaction.objectStore(EPISODE_STORE);
    const events = transaction.objectStore(EVENT_STORE);

    for (const { candidate, fingerprint } of pending) {
      episodes.put(candidate);
      const event: CandidateArchiveEvent = {
        key: `${candidate.id}:${fingerprint}`,
        candidateId: candidate.id,
        symbol: candidate.symbol,
        timestamp: candidate.lastUpdatedAt,
        state: candidate.state,
        direction: candidate.direction,
        family: candidate.family,
        score: candidate.candidateScore,
        price: candidate.currentPrice,
        freshIssuanceAllowed: candidate.freshIssuanceAllowed,
        deepValidated: candidate.deepValidated,
        validationStatus: candidate.validationStatus,
        validationCoverage: candidate.validationCoverage,
        validationIssueCount: candidate.validationIssues.length,
        reason: candidate.transitionReason ?? candidate.statusNote,
      };
      events.put(event);
    }

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Unable to archive candidates.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Candidate archive transaction aborted.'));
    });
    for (const { candidate, fingerprint } of pending) lastWrittenFingerprints.set(candidate.id, fingerprint);
  } finally {
    database.close();
  }
}

export async function getCandidateEpisodes(symbol: string, limit = 12): Promise<CandidateRecord[]> {
  if (typeof indexedDB === 'undefined') return [];
  const database = await openDatabase();
  try {
    const transaction = database.transaction(EPISODE_STORE, 'readonly');
    const index = transaction.objectStore(EPISODE_STORE).index('symbol');
    const rows = (await requestValue(index.getAll(symbol))) as CandidateRecord[];
    return rows.sort((left, right) => right.foundAt - left.foundAt).slice(0, limit);
  } finally {
    database.close();
  }
}

export async function getCandidateEvents(candidateId: string, limit = 20): Promise<CandidateArchiveEvent[]> {
  if (typeof indexedDB === 'undefined') return [];
  const database = await openDatabase();
  try {
    const transaction = database.transaction(EVENT_STORE, 'readonly');
    const index = transaction.objectStore(EVENT_STORE).index('candidateId');
    const rows = (await requestValue(index.getAll(candidateId))) as CandidateArchiveEvent[];
    return rows.sort((left, right) => right.timestamp - left.timestamp).slice(0, limit);
  } finally {
    database.close();
  }
}


const TERMINAL_STATES = new Set<CandidateRecord['state']>(['expired', 'invalidated', 'reversed']);

export async function expireArchivedCandidates(now = Date.now()): Promise<number> {
  if (typeof indexedDB === 'undefined') return 0;
  const database = await openDatabase();
  try {
    const transaction = database.transaction([EPISODE_STORE, EVENT_STORE], 'readwrite');
    const episodes = transaction.objectStore(EPISODE_STORE);
    const events = transaction.objectStore(EVENT_STORE);
    const rows = (await requestValue(episodes.getAll())) as CandidateRecord[];
    let expiredCount = 0;

    for (const row of rows) {
      if (row.expiresAt > now || TERMINAL_STATES.has(row.state)) continue;
      const expired: CandidateRecord = {
        ...row,
        state: 'expired',
        statusNote: 'The selected prediction horizon ended. Any later opportunity must be recorded as a new candidate episode.',
        transitionReason: row.transitionReason ?? 'The candidate horizon expired without rewriting the original discovery record.',
        lastUpdatedAt: Math.max(row.lastUpdatedAt, row.expiresAt),
      };
      episodes.put(expired);
      events.put({
        key: `${row.id}:expired`,
        candidateId: row.id,
        symbol: row.symbol,
        timestamp: row.expiresAt,
        state: 'expired',
        direction: row.direction,
        family: row.family,
        score: row.candidateScore,
        price: row.currentPrice,
        freshIssuanceAllowed: false,
        deepValidated: row.deepValidated,
        validationStatus: row.validationStatus,
        validationCoverage: row.validationCoverage,
        validationIssueCount: row.validationIssues.length,
        reason: expired.transitionReason ?? expired.statusNote,
      } satisfies CandidateArchiveEvent);
      expiredCount += 1;
    }

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Unable to expire archived candidates.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Candidate expiry transaction aborted.'));
    });
    return expiredCount;
  } finally {
    database.close();
  }
}
